import React, { useState } from 'react';
import { Card, CardContent, CardHeader } from './ui/card';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Reusable collapsible card for sections that carry a lot of information but
 * don't need to stay open all the time — keeps mobile screens uncluttered.
 * Collapsed by default; tap the header to expand/collapse.
 *
 * Props:
 *   title, subtitle   — header text (subtitle shows only when collapsed)
 *   icon              — optional lucide icon component
 *   badge             — optional count/label pill in the header
 *   defaultOpen       — start expanded (default false)
 *   className, headerClassName, testId, children
 */
export default function CollapsibleSection({
  title,
  subtitle,
  icon: Icon,
  badge,
  defaultOpen = false,
  className = '',
  headerClassName = '',
  testId,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className={className} data-testid={testId}>
      <CardHeader
        className={`py-3 px-4 cursor-pointer select-none ${headerClassName}`}
        role="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0 flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 shrink-0" />}
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{title}</p>
              {subtitle && !open && (
                <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p>
              )}
            </div>
            {badge != null && badge !== '' && (
              <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-cyan-100 text-cyan-700 text-xs font-semibold">
                {badge}
              </span>
            )}
          </div>
          {open
            ? <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" data-testid={testId ? `${testId}-chevron-up` : undefined} />
            : <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" data-testid={testId ? `${testId}-chevron-down` : undefined} />}
        </div>
      </CardHeader>
      {open && <CardContent className="px-4 pb-4">{children}</CardContent>}
    </Card>
  );
}
